// Which of the laundrette's washers are going round, and where he stands to watch one (Sam, 3 Nov: "a graphic of
// washing in the washing machine that spins with the active machines, and he watches the ones that are active...it's
// a small detail, but would make a big difference i think"). Read by the page (components/three/world/
// LaundretteThings.jsx draws the washing going round behind the doors) and by the town (sim-server
// bridge/BridgeAction.js sends him to stand in front of one that is going, and npc/Actions.js turns him to it), so
// both say the same machines at the same moment: it is worked out from the clock, and nothing is sent.

import { MACHINES, ISLAND_Y } from './laundretteThings.js'
import { LAUNDRETTE_ROOT } from './laundrette.js'

// The sixteen washers, in the pack's own units (lib/world/laundretteThings.js says how they are read off the blend):
// the six along the back wall, and the island's ten, five facing the dryers and five the window. `door` is the middle
// of the drum just inside its glass, `face` the way the door faces, and `stand` where he stands to watch it, a metre
// and a half out. The back row has none: between it and the island is too narrow for his walk (sim-server NavGrid.js).
export const WASHERS = [
  ...Object.entries(MACHINES).map(([id, x]) => ({ id, door: [x, 6.18, 0.625], face: '-y', stand: null })),
  ...ISLAND_Y.map((y, i) => ({ id: `w${i + 1}`, door: [0.37, y, 0.625], face: '-x', stand: [-0.68, y] })),
  ...ISLAND_Y.map((y, i) => ({ id: `e${i + 1}`, door: [2.07, y, 0.625], face: '+x', stand: [3.12, y] })),
]

// A wash goes round for half an hour, the last part of it the spin, and each machine is started at times of its own,
// every 70 to 110 minutes: about a third of them are going at any moment
export const WASH_MS = 30 * 60_000
const SPIN_FROM = 0.85

const hash = (s) => {
  let h = 2166136261
  for (const c of s) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619) }
  return h >>> 0
}

/** The washers going round now: which, how far through (0..1), the time left (ms) and whether it is the spin. */
export function runningAt(now = Date.now()) {
  const out = []
  for (const m of WASHERS) {
    const h = hash(m.id)
    const every = (70 + (h % 41)) * 60_000
    const into = (now + ((h >>> 8) % 1000) / 1000 * every) % every
    if (into < WASH_MS) out.push({ id: m.id, through: into / WASH_MS, left: WASH_MS - into, spin: into / WASH_MS > SPIN_FROM })
  }
  return out
}

/** What is in a washer, by the colour the page tints its washing: a machine of whites, darks, or colours. */
export function loadOf(id) {
  return ['#ffffff', '#6f7a90', '#eaa9bd', '#a3c4ea', '#d6dfa0', '#f0d9a8', '#ffffff', '#b8a0d8'][hash(`${id}:load`) % 8]
}

/** The pack's (x, y) in the town's (x, z): Sam's building turned and scaled (LAUNDRETTE_ROOT). */
export function townXZ([x, y]) {
  const { x: ox, z: oz, rotY, scale } = LAUNDRETTE_ROOT
  const c = Math.cos(rotY)
  const s = Math.sin(rotY)
  const tx = x * scale
  const tz = -y * scale
  return { x: ox + c * tx + s * tz, z: oz - s * tx + c * tz }
}

/**
 * A washer going round that he can stand at, with at least `need` ms of its wash still to go: the one with the most
 * left, so he settles in front of a whole wash. Where to stand (the town's x, z) and which way to face (rotY, as the
 * town turns him: atan2 of the way across, then along). Null when none is going.
 */
export function machineToWatch(now = Date.now(), need = 8 * 60_000) {
  let best = null
  for (const r of runningAt(now)) {
    const m = WASHERS.find((w) => w.id === r.id)
    if (!m.stand || r.left < need || (best && best.left >= r.left)) continue
    best = { ...r, m }
  }
  if (!best) return null
  const stand = townXZ(best.m.stand)
  const door = townXZ(best.m.door)
  return { id: best.id, stand, yaw: Math.atan2(door.x - stand.x, door.z - stand.z), left: best.left }
}
